import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Exercise, EnhancedExercise } from '../types/exercise';
import { exerciseService } from '../services/exerciseService';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Plus, Calendar, Clock, Users, FileText, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Dashboard: React.FC = () => {
  const [exercises, setExercises] = useState<(Exercise | EnhancedExercise)[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadExercises();
  }, [user]);

  const loadExercises = async () => {
    if (!user) return;
    
    try {
      const userExercises = await exerciseService.getUserExercises(user.uid);
      setExercises(userExercises);
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getStatusBadgeColor = (status: Exercise['status']) => {
    switch (status) {
      case 'Planned':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading exercises...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: '#2563eb', 
        color: 'white',
        padding: '20px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              margin: '0 0 4px 0'
            }}>
              Crisis Compass
            </h1>
            <p style={{ 
              fontSize: '16px',
              opacity: 0.9,
              margin: 0
            }}>
              Exercise Creator
            </p>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px'
          }}>
            <span style={{ fontSize: '14px', opacity: 0.9 }}>
              {user?.email}
            </span>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white'
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '32px 20px' 
      }}>
        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '20px', 
          marginBottom: '40px' 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '24px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FileText style={{ 
                height: '32px', 
                width: '32px', 
                color: '#2563eb' 
              }} />
              <div style={{ marginLeft: '16px' }}>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#6b7280',
                  margin: '0 0 4px 0'
                }}>
                  Total Exercises
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#111827',
                  margin: 0
                }}>
                  {exercises.length}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '24px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Calendar style={{ 
                height: '32px', 
                width: '32px', 
                color: '#059669' 
              }} />
              <div style={{ marginLeft: '16px' }}>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#6b7280',
                  margin: '0 0 4px 0'
                }}>
                  Planned
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#111827',
                  margin: 0
                }}>
                  {exercises.filter(e => e.status === 'Planned').length}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '24px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Clock style={{ 
                height: '32px', 
                width: '32px', 
                color: '#d97706' 
              }} />
              <div style={{ marginLeft: '16px' }}>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#6b7280',
                  margin: '0 0 4px 0'
                }}>
                  In Progress
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#111827',
                  margin: 0
                }}>
                  {exercises.filter(e => e.status === 'In Progress').length}
                </p>
              </div>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: 'white', 
            padding: '24px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Users style={{ 
                height: '32px', 
                width: '32px', 
                color: '#7c3aed' 
              }} />
              <div style={{ marginLeft: '16px' }}>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#6b7280',
                  margin: '0 0 4px 0'
                }}>
                  Completed
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  fontWeight: 'bold', 
                  color: '#111827',
                  margin: 0
                }}>
                  {exercises.filter(e => e.status === 'Completed').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Create New Exercise Section */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#111827',
            margin: 0
          }}>
            Your Exercises
          </h2>
          <Button 
            onClick={() => navigate('/exercises/new')}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus className="w-5 h-5" />
            Create New Exercise
          </Button>
        </div>

        {/* Empty State */}
        {exercises.length === 0 ? (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '80px 40px', 
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <FileText style={{ 
              height: '64px', 
              width: '64px', 
              color: '#9ca3af',
              margin: '0 auto 24px auto'
            }} />
            <h3 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827',
              margin: '0 0 8px 0'
            }}>
              No exercises yet
            </h3>
            <p style={{ 
              fontSize: '16px',
              color: '#6b7280',
              margin: '0 0 32px 0'
            }}>
              Get started by creating your first crisis exercise.
            </p>
            <Button 
              onClick={() => navigate('/exercises/new')}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                padding: '16px 32px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Plus className="w-5 h-5" />
              Create Your First Exercise
            </Button>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
            gap: '24px' 
          }}>
            {exercises.map((exercise) => (
              <div 
                key={exercise.id} 
                onClick={() => navigate(`/exercises/${exercise.id}`)}
                style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{ padding: '24px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    marginBottom: '12px' 
                  }}>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: '#111827',
                      margin: 0,
                      flex: 1
                    }}>
                      {(exercise as EnhancedExercise).exerciseTitle || (exercise as Exercise).title || 'Untitled Exercise'}
                    </h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: exercise.status === 'Planned' ? '#dbeafe' : 
                                     exercise.status === 'In Progress' ? '#fef3c7' : '#dcfce7',
                      color: exercise.status === 'Planned' ? '#1d4ed8' : 
                             exercise.status === 'In Progress' ? '#d97706' : '#16a34a'
                    }}>
                      {exercise.status}
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 16px 0'
                  }}>
                    {exercise.scenarioType} • {(exercise as Exercise).exerciseDate ? formatDate((exercise as Exercise).exerciseDate) + ' at ' + (exercise as Exercise).exerciseTime : 'Date not set'}
                  </p>
                  <p style={{ 
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5'
                  }}>
                    {(exercise as EnhancedExercise).sections?.scenarioSummary?.substring(0, 120) || (exercise as Exercise).description?.substring(0, 120) || 'No description available'}...
                  </p>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontSize: '12px',
                    color: '#9ca3af'
                  }}>
                    <span>{(exercise as EnhancedExercise).objectives?.length || (exercise as Exercise).objectives?.length || 0} objectives</span>
                    <span>{(exercise as EnhancedExercise).sections?.injects?.length || (exercise as Exercise).injects?.length || 0} injects</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
